import { combineReducers } from "redux";
import { persistReducer, createTransform } from "redux-persist";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
import { entitiesReducer } from "./entities";
import { featuresReducer } from "./features";
import { adminReducers } from "./admin";
import { LANGUAGE } from "@root/constants";

/**
 * Redux Persist is a library that allows saving a Redux store in the local storage of an application,
 * So that application data don't lost when it relaunch.
 * As we have seprated our reducers into features and entities,
 * So for both features and entities we have to define seprate persistor configs too
 * to persist nested reducers
 *
 * Using sessionStorage for tab-specific state to support multi-user
 */

// Create a noop storage for SSR
const createNoopStorage = () => {
  return {
    getItem(_key: string) {
      return Promise.resolve(null);
    },
    setItem(_key: string, value: any) {
      return Promise.resolve(value);
    },
    removeItem(_key: string) {
      return Promise.resolve();
    },
  };
};

// Use sessionStorage for tab-specific state (multi-user support)
const sessionStorage =
  typeof window !== "undefined"
    ? createWebStorage("session")
    : createNoopStorage();

const localStorage =
  typeof window !== "undefined"
    ? createWebStorage("local")
    : createNoopStorage();

// Strip language from persisted auth state so it always resets to Korean on reload
const stripLanguageTransform = createTransform(
  (inboundState: any) => {
    if (inboundState?.data) {
      const { language, ...rest } = inboundState.data;
      return { ...inboundState, data: rest };
    }
    return inboundState;
  },
  (outboundState: any) => {
    if (outboundState?.data) {
      return { ...outboundState, data: { ...outboundState.data, language: LANGUAGE.KOREAN } };
    }
    return outboundState;
  },
  { whitelist: ["auth"] }
);

const featurePersistConfig = {
  key: "features",
  version: 1,
  storage: localStorage,
  whitelist: ["auth"],
  transforms: [stripLanguageTransform],
};

const entityPersistConfig = {
  key: "entities",
  version: 1,
  storage: sessionStorage,
  whitelist: ["auth"],
};

const adminPersistConfig = {
  key: "admin",
  version: 1,
  storage: sessionStorage,
  whitelist: ["user"],
};

const rootReducer = combineReducers({
  entities: persistReducer(entityPersistConfig, entitiesReducer),
  features: persistReducer(featurePersistConfig, featuresReducer),
  admin: persistReducer(adminPersistConfig, adminReducers),
});

export default rootReducer;
